import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import PostSaleCasesAPI from 'dashboard/api/postSaleCases';
import {
  AGENDA_BUCKETS,
  calendarCells,
  casesForDate,
  countsForDate,
  dateFromMonthInput,
  dateKey,
  presentAgendaCases,
  shiftMonth,
} from './postSaleAgendaPresenter';

const initialBucketFilters = () =>
  AGENDA_BUCKETS.reduce(
    (filters, bucket) => ({ ...filters, [bucket]: true }),
    {}
  );

const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

export function usePostSaleAgenda({ t, notify }) {
  const cases = ref([]);
  const loading = ref(false);
  const addressFilters = ref({
    address_street: '',
    address_number: '',
    address_unit: '',
  });
  const addressOptions = ref([]);
  const loadingAddresses = ref(false);
  const selectedDate = ref(new Date());
  const responsibleUserId = ref('');
  const enabledBuckets = ref(initialBucketFilters());
  const lastUpdatedAt = ref(null);
  const currentTime = ref(Date.now());
  let refreshTimer;

  const minutesSinceRefresh = computed(() => {
    if (!lastUpdatedAt.value) return null;

    return Math.max(
      0,
      Math.floor((currentTime.value - lastUpdatedAt.value.getTime()) / 60000)
    );
  });

  const load = async () => {
    loading.value = true;
    try {
      const params = {};
      if (responsibleUserId.value)
        params.responsible_user_id = responsibleUserId.value;
      if (addressFilters.value.address_street)
        params.address_street = addressFilters.value.address_street;
      if (addressFilters.value.address_number)
        params.address_number = addressFilters.value.address_number;
      if (addressFilters.value.address_unit)
        params.address_unit = addressFilters.value.address_unit;

      const { data } = await PostSaleCasesAPI.agenda(params);
      cases.value = presentAgendaCases(data?.data || []);
      lastUpdatedAt.value = new Date();
      currentTime.value = Date.now();
    } catch (error) {
      cases.value = [];
      notify(messageFrom(error, t('POST_SALE.AGENDA.ALERTS.LOAD_ERROR')));
    } finally {
      loading.value = false;
    }
  };

  const selectDate = value => {
    selectedDate.value = new Date(value);
  };
  const goToToday = () => selectDate(new Date());
  const previousMonth = () => selectDate(shiftMonth(selectedDate.value, -1));
  const nextMonth = () => selectDate(shiftMonth(selectedDate.value, 1));
  const selectMonth = value =>
    selectDate(dateFromMonthInput(value, selectedDate.value));
  const setResponsible = value => {
    responsibleUserId.value = value;
    return load();
  };
  const setAddress = value => {
    addressFilters.value = {
      address_street: value?.address_street || '',
      address_number: value?.address_number || '',
      address_unit: value?.address_unit || '',
    };
    return load();
  };
  const loadAddresses = async query => {
    if (!query?.trim()) {
      addressOptions.value = [];
      loadingAddresses.value = false;
      return;
    }

    loadingAddresses.value = true;
    try {
      const { data } = await PostSaleCasesAPI.addresses({
        agenda: true,
        q: query,
      });
      addressOptions.value = data?.data || [];
    } catch {
      addressOptions.value = [];
    } finally {
      loadingAddresses.value = false;
    }
  };
  const toggleBucket = bucket => {
    enabledBuckets.value = {
      ...enabledBuckets.value,
      [bucket]: !enabledBuckets.value[bucket],
    };
  };

  const selectedCases = computed(() =>
    casesForDate(cases.value, selectedDate.value, enabledBuckets.value)
  );
  const selectedCounts = computed(() =>
    countsForDate(cases.value, selectedDate.value)
  );
  const todayCounts = computed(() => countsForDate(cases.value, new Date()));
  const days = computed(() => calendarCells(selectedDate.value, cases.value));
  const monthValue = computed(() => dateKey(selectedDate.value).slice(0, 7));

  onMounted(() => {
    refreshTimer = window.setInterval(() => {
      currentTime.value = Date.now();
    }, 60000);
  });
  onBeforeUnmount(() => window.clearInterval(refreshTimer));

  return {
    addressFilters,
    addressOptions,
    cases,
    days,
    enabledBuckets,
    goToToday,
    lastUpdatedAt,
    load,
    loadAddresses,
    loading,
    loadingAddresses,
    minutesSinceRefresh,
    monthValue,
    nextMonth,
    previousMonth,
    responsibleUserId,
    selectDate,
    selectedCases,
    selectedCounts,
    selectedDate,
    selectMonth,
    setAddress,
    setResponsible,
    todayCounts,
    toggleBucket,
  };
}
